import React, { useMemo } from 'react';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { Task } from 'shared/types/task';
import { TaskActionGoal } from 'shared/types/task-action';
import { getCompletionPercentage } from 'shared/utils/get-completion-percentage';
import { getGoalCompletion } from 'shared/utils/get-goal-completion';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';

import { ActionTaskContextMenu } from './action-task-context-menu';
import * as Styled from './event.style';

export type GoalEventProps = {
  task: Task;
  goals: Goal[];
  lifeAreas: LifeArea[];
  onClick?: (id: ID) => void;
  marginTop: number;
  lineHeight: number;
};

export const TaskGoalEvent: React.FC<GoalEventProps> = ({
  task,
  goals,
  lifeAreas,
  onClick,
  marginTop,
  lineHeight,
}) => {
  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState();
  const _onClick = () => onClick?.(task.id);

  const goal = useMemo(
    () =>
      goals.find(({ id }) => (task.action as TaskActionGoal)?.meta?.id === id),
    [goals, task.action],
  );

  const completionPercentage = useMemo(
    () =>
      goal
        ? getCompletionPercentage(
            getGoalCompletion(goal, {
              goals,
            }),
          )
        : undefined,
    [goal, goals],
  );

  const color = useMemo(
    () =>
      goal
        ? getLinkedItemColor(goal.id, { goals, lifeAreas }) ?? undefined
        : undefined,
    [goal, goals, lifeAreas],
  );

  if (!goal) {
    return null;
  }

  const completed = !!task.completedAt;

  return (
    <>
      <Styled.Container
        onClick={_onClick}
        onContextMenu={openMenu}
        $color={color}
        $completed={completed}
      >
        <Styled.Content>
          <Styled.IconContainer $color={color}>
            <CircleProgress
              percentage={completionPercentage}
              hidePercentage
              isSmall
            />
          </Styled.IconContainer>
          <Styled.Title
            $marginTop={marginTop}
            $lineHeight={lineHeight}
            $completed={completed}
          >
            {goal.name}
          </Styled.Title>
        </Styled.Content>
      </Styled.Container>

      {!!menuPosition && (
        <ActionTaskContextMenu
          task={task}
          onClose={closeMenu}
          location={menuPosition}
        />
      )}
    </>
  );
};
